import { useEffect, useCallback } from 'react';
import { Layout } from 'antd';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ActionCableProvider } from 'react-actioncable-provider';
import { createConsumer } from '@rails/actioncable';

import { loadFromLocalStorage } from 'utils/localStorageHelpers';

import Header from 'components/molecules/Header';
import NoIndex from 'components/atoms/NoIndex';
import UserInfo from 'components/molecules/UserInfo';
import user from 'redux/user';
import cart from 'redux/cart';
import SessionPopup from './components/SessionPopup';

import './index.scss';

const DefaultLayout = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const currentUser = useSelector(user.selectors.getCurrentUser);
  const errors = useSelector(user.selectors.getErrors);
  const showWelcomeMsg = loadFromLocalStorage('showWelcomeMsg');
  const authHeaders = loadFromLocalStorage('authHeaders');
  const cable = createConsumer(
    `${process.env.REACT_APP_CABLE_BASE_URL}?token=${authHeaders?.token}&client=${authHeaders?.client}&uid=${authHeaders?.uid}`,
  );

  useEffect(() => {
    dispatch(user.thunks.fetchUser());
  }, [dispatch, children]);

  /* Once a session, not on every navigation like the user above: the menu's
     cart count has to be right on the first screen the client lands on, and
     after that every write answers with the cart it left behind, so the store
     stays current without asking again. */
  useEffect(() => {
    dispatch(cart.thunks.fetch({ show_all: true }));
  }, [dispatch]);

  const logout = useCallback(() => {
    dispatch(user.actions.signout());
    history.push('clients/sign_in');
  }, [dispatch, history]);

  return (
    <ActionCableProvider cable={cable}>
      <Layout className="site-layout">
        <NoIndex />
        <Header logout={logout} errors={errors} />
        <UserInfo name={currentUser.full_name} balance={currentUser.balance} phoneNumber={currentUser.phone_1} />
        <Layout.Content>
          <TransitionGroup>
            <CSSTransition key={location} timeout={1000} classNames="fade">
              <div className="content-layout">{children}</div>
            </CSSTransition>
          </TransitionGroup>
        </Layout.Content>
        {/* One popup per sign-in. SessionPopup picks between a live notice and the welcome
            message; the flag only says that this session hasn't been interrupted yet. */}
        {showWelcomeMsg && <SessionPopup />}
      </Layout>
    </ActionCableProvider>
  );
};

export default DefaultLayout;
